import { ImageFilter } from "../models/Post";
import { IMAGE_FILTER_OPTIONS, imageFilterToCss } from "../utils/imageFilter";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";

interface ImageFilterPickerProps {
  /** URL to render in each preview thumbnail. When omitted, falls back to a checker pattern. */
  previewUrl?: string | null;
  /** Currently selected filter (null/undefined = no filter). */
  value: ImageFilter | null | undefined;
  onChange: (value: ImageFilter | undefined) => void;
  label?: string;
  /** Compact size for tighter UI sections (e.g., slide bg controls). */
  size?: "default" | "sm";
}

/**
 * Filter selector — small thumbnails of the actual image under each preset,
 * with a magazine-style underline beneath the active label rather than a tile-wide ring.
 * Falls back to a warm checker pattern when no preview is available.
 */
export function ImageFilterPicker({
  previewUrl,
  value,
  onChange,
  label = "Filter",
  size = "default",
}: ImageFilterPickerProps) {
  const active: ImageFilter | null = value ?? null;
  const thumbHeight = size === "sm" ? "h-12" : "h-14";
  const labelSize = size === "sm" ? "text-[10.5px]" : "text-[11.5px]";

  // Warm-tinted checker for the "no image yet" state — uses paper-leaning OKLCH
  // neutrals so it sits inside the same palette as the rest of the surface.
  const checkerFill = [
    "conic-gradient(from 90deg at 50% 50%,",
    "oklch(0.93 0.006 60) 25%,",
    "oklch(0.97 0.004 60) 0 50%,",
    "oklch(0.93 0.006 60) 0 75%,",
    "oklch(0.97 0.004 60) 0)",
  ].join(" ");

  return (
    <div className="space-y-2">
      {label && (
        <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </Label>
      )}
      <div
        role="radiogroup"
        aria-label={label}
        className="grid grid-cols-4 gap-2"
      >
        {IMAGE_FILTER_OPTIONS.map((opt) => {
          const isActive = opt.value === active;
          const filterCss = imageFilterToCss(opt.value);
          return (
            <button
              key={opt.value ?? "none"}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange(opt.value ?? undefined)}
              className={cn(
                "group flex flex-col items-stretch gap-2 text-left",
                "focus:outline-none",
                // Subtle press feedback — keeps it from feeling like a clickable card
                "active:[&_.thumb]:scale-[0.985]"
              )}
            >
              {/* Thumbnail — built as a wrapper "frame" (background color) +
                  inner photo with 1px inset, so the frame lives INSIDE the
                  element bounds. This avoids clipping when the tile sits flush
                  against a panel edge (which is why ring/outline don't work
                  here). The 1px gap between photo and frame still reads as
                  the "framed image" look the design wants. */}
              <div
                className={cn(
                  thumbHeight,
                  "thumb relative w-full rounded-[6px] p-px transition-colors duration-150 ease-out",
                  isActive
                    ? "bg-foreground/90"
                    : "bg-foreground/15 group-hover:bg-foreground/45",
                  "group-focus-visible:bg-primary"
                )}
              >
                <div
                  className="h-full w-full overflow-hidden rounded-[5px]"
                  style={{
                    backgroundImage: previewUrl ? `url(${previewUrl})` : checkerFill,
                    backgroundSize: previewUrl ? "cover" : "10px 10px",
                    backgroundPosition: "center",
                    filter: filterCss,
                  }}
                />
              </div>
              {/* Label — orange underline on active. The underline tracks the
                  word width, not the tile width — gives it an editorial feel.
                  Reserve underline space always so height stays stable. */}
              <span
                className={cn(
                  "self-start pb-[2px] border-b-[1.5px] transition-colors duration-150",
                  labelSize,
                  "tracking-[0.01em]",
                  isActive
                    ? "border-primary text-foreground font-medium"
                    : "border-transparent text-muted-foreground group-hover:text-foreground/85"
                )}
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
